import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertTitle,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { Popover } from "../../common/popover/Popover";
import { useToast } from "../../common/hooks/useToast";
import { getRateLimitRejection } from "../../common/rateLimit";
import { Item } from "../../styles/common";
import { APP_CONFIG_ROWS } from "./appConfig.queries";
import { SET_MIN_APP_VERSION } from "./appConfig.mutation";
// The rejection carries `extensions.field`, exactly as the tuning form's does,
// so the same reader puts the message on the input instead of in a banner.
import { fieldErrorOf } from "./nearbyAlerts";
import {
  BINARY_VERSION_NOTICE,
  LAUNCH_NOTICE,
  NO_AUDIT_NOTICE,
  STAGING_NOTICE,
  VERSION_FORMAT_HINT,
  describeEnforcement,
  describeMinVersionFailure,
  normalizeVersion,
  readMinVersion,
  summarizeMinVersion,
  validateVersion,
} from "./minVersion";

/**
 * The forced-upgrade control: the `minVersion` row, set and cleared from here.
 *
 * Its own component, with its own read, because it shares nothing with the
 * nearby-alerts tuning beside it but the table it lives in — and because it has
 * to stay usable when that tuning query fails. This is the control somebody
 * reaches for during an incident; it must not disappear behind another
 * screen's error state.
 *
 * Nothing polls. The value only changes when an admin changes it, and a
 * countdown or a refresh loop would add nothing but requests.
 */
export const MinVersionPanel: React.FC = (): JSX.Element => {
  const { data, loading, error, refetch } = useQuery(APP_CONFIG_ROWS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [setMinAppVersion] = useMutation(SET_MIN_APP_VERSION);
  const toast = useToast();

  /**
   * What the last write stored, which outranks the query's copy.
   *
   * The mutation returns a bare string, so the cache cannot patch a row it
   * cannot identify, and re-reading to find out what we just wrote is a request
   * spent to learn something the response already said. Held as a one-element
   * wrapper so a cleared row (`null`) is distinguishable from "no write yet".
   */
  const [written, setWritten] = useState<{ value: string | null } | null>(null);

  /** `null` until the admin types — the input mirrors the stored value until then. */
  const [draft, setDraft] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const stored: string | null = written
    ? written.value
    : readMinVersion(data?.fetchAppConfig);

  /** True while the read failed and no write has told us what is stored. */
  const unknown = Boolean(error) && !written;

  const value = draft ?? stored ?? "";
  const enforcement = useMemo(() => describeEnforcement(stored), [stored]);

  const dirty = normalizeVersion(value) !== normalizeVersion(stored ?? "");

  /**
   * Clearing stays available when the read failed.
   *
   * A failed read leaves `stored` null, which is indistinguishable from "no row"
   * — and disabling the way out of a forced upgrade because the *lookup* broke
   * is the wrong failure. Clearing a row that was never set is a no-op on the
   * server, not an error, so the cost of offering it needlessly is nothing.
   */
  const canClear = Boolean(stored) || unknown;

  const handleChange = useCallback((next: string) => {
    setDraft(next);
    setFieldError("");
  }, []);

  const write = useCallback(
    async (version: string | null) => {
      setSaving(true);

      try {
        const result = await setMinAppVersion({ variables: { version } });

        // The server answers with what it stored, so this is the outcome rather
        // than an echo of the request.
        const next: string | null = result.data?.setMinAppVersion ?? null;

        toast.success(summarizeMinVersion(next, stored));
        setWritten({ value: next });
        setDraft(null);
        setFieldError("");
      } catch (writeError) {
        // The row is untouched on every failure path, so nothing on screen is
        // stale — leave the stored value showing and put the reason on the input
        // when the server named one.
        const named = fieldErrorOf(writeError);

        if (named) {
          setFieldError(named.message);
        } else if (!getRateLimitRejection(writeError)) {
          // A rate-limit rejection is announced by the global Apollo error link
          // already, in the same words; toasting it again would double it.
          toast.error(describeMinVersionFailure(writeError, version === null));
        }
      } finally {
        setSaving(false);
        setConfirming(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMinAppVersion, stored]
  );

  /**
   * Validated before the dialog opens, not after it is confirmed: a typo is
   * worth catching before somebody reads a warning about locking the fleet out
   * and agrees to it.
   */
  const handleSetClick = useCallback(() => {
    const message = validateVersion(value);

    if (message) {
      setFieldError(message);
      return;
    }

    setConfirming(true);
  }, [value]);

  const handleRefresh = useCallback(async () => {
    try {
      await refetch();
      // Dropped only once the read came back, so a failed refresh does not
      // replace a confirmed write with a stale row.
      setWritten(null);
    } catch {
      // `error` from the hook renders it; nothing extra to say here.
    }
  }, [refetch]);

  return (
    <Item sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h5">Forced app update</Typography>

        <Button
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={handleRefresh}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The lowest app version still allowed. Below it the update prompt loses
        its <strong>Ignore</strong> and <strong>Later</strong> buttons and cannot
        be dismissed. Nothing on the server enforces this — the app reads the
        value at launch and applies it, and ignores anything it cannot parse.
      </Typography>

      {error && <ErrorNotifier error={error} />}

      {/*
       * A failed read is reported as not knowing, never as "no forced upgrade".
       * `stored` is null in both cases, and the reassuring one is the lie: an
       * upgrade could be in force right now and this screen cannot see it.
       */}
      {unknown ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Cannot tell what is set</AlertTitle>
          <Typography variant="body2">
            The read failed, so this screen does not know whether an upgrade is
            being forced. Refresh to find out. Setting or clearing a value still
            works — neither depends on this read.
          </Typography>
        </Alert>
      ) : (
        <Alert severity={enforcement.severity} sx={{ mb: 2 }}>
          <AlertTitle>{enforcement.title}</AlertTitle>
          <Typography variant="body2">{enforcement.text}</Typography>
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          label="Minimum app version"
          placeholder="4.0.88"
          value={value}
          disabled={saving}
          error={Boolean(fieldError)}
          helperText={fieldError || VERSION_FORMAT_HINT}
          inputProps={{ "aria-label": "Minimum app version" }}
          sx={{ minWidth: 260 }}
          onChange={(event) => handleChange(event.target.value)}
        />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 0.25 }}>
          <Button
            variant="contained"
            color="warning"
            disabled={!dirty || saving}
            onClick={handleSetClick}
          >
            {saving ? "Saving…" : "Set minimum version"}
          </Button>

          {/*
           * Deleting the row, not writing an empty one: an empty row reads as
           * "somebody meant something here" to whoever looks at the table next,
           * and this is what people read during an incident.
           */}
          <Button
            color="error"
            disabled={!canClear || saving}
            onClick={() => write(null)}
          >
            Stop forcing an upgrade
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 1 }}>
        <Typography variant="body2">{BINARY_VERSION_NOTICE}</Typography>
      </Alert>

      <Alert severity="info">
        <Typography variant="body2">{NO_AUDIT_NOTICE}</Typography>
      </Alert>

      {/* ---------------------------------------------- Confirmation */}
      <Popover open={confirming} handleClose={() => setConfirming(false)}>
        {/*
         * `Item`, not `Box`: reactjs-popup's stylesheet is never imported, so
         * the popup wrapper is transparent and the content brings its own
         * opaque surface.
         */}
        <Item sx={{ p: 3, maxWidth: 560, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Force an upgrade to {normalizeVersion(value)}?
          </Typography>

          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {LAUNCH_NOTICE}
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">{STAGING_NOTICE}</Typography>
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {BINARY_VERSION_NOTICE}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirming(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={saving}
              onClick={() => write(normalizeVersion(value))}
            >
              {saving ? "Saving…" : `Force ${normalizeVersion(value)}`}
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Item>
  );
};
